'use client'

import { BriefcaseMedical, Building2, Check, ChevronDown } from 'lucide-react'
import { cn } from '@/utilities/ui'
import type { SwitcherOrgViewModel } from '@/utilities/dashboard/orgProgramSwitcherTree'
import {
  FOCUS_VISIBLE_RING,
  IconTile,
  MatchBadge,
  ROW_HOVER_CLASS,
} from './OrgProgramSwitcherParts'

export type OrgProgramSwitcherMobileProps = {
  filtered: SwitcherOrgViewModel[]
  expandedOrgId: string | null
  selectedOrgId: string | null
  selectedProgramId: string | null
  isOrgSelected: boolean
  onToggleOrg: (orgId: string) => void
  onSelectOrg: (orgId: string) => void
  onSelectProgram: (orgId: string, programId: string) => void
}

export function OrgProgramSwitcherMobile({
  filtered,
  expandedOrgId,
  selectedOrgId,
  selectedProgramId,
  isOrgSelected,
  onToggleOrg,
  onSelectOrg,
  onSelectProgram,
}: Readonly<OrgProgramSwitcherMobileProps>) {
  return (
    <div
      className="flex max-h-[min(70vh,36rem)] flex-col gap-1 overflow-y-auto sm:hidden"
      data-testid="org-program-switcher-mobile"
    >
      <p className="m-0 mb-3 text-[11px] font-medium tracking-wider text-cp-placeholder uppercase">
        Organizations
      </p>
      {filtered.map((org) => {
        const isExpanded = expandedOrgId === org.id
        const isSelectedOrg = selectedOrgId === org.id
        const programPanelId = `org-program-switcher-mobile-panel-${org.id}`
        return (
          <div key={org.id}>
            <button
              type="button"
              data-testid={`org-program-switcher-mobile-org-${org.id}`}
              aria-current={isSelectedOrg ? 'true' : undefined}
              aria-expanded={isExpanded}
              aria-controls={programPanelId}
              data-selected={isSelectedOrg ? 'true' : undefined}
              onClick={() => onToggleOrg(org.id)}
              className={cn(
                'flex h-14 w-full cursor-pointer items-center justify-between rounded-lg px-2 text-left transition-colors',
                FOCUS_VISIBLE_RING,
                isExpanded || isSelectedOrg ? 'bg-cp-light-purple' : ROW_HOVER_CLASS,
              )}
            >
              <span className="flex min-w-0 items-center gap-4">
                <IconTile>
                  <Building2 className="h-5 w-5 text-cp-bright-blue" aria-hidden="true" />
                </IconTile>
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block truncate text-sm font-medium',
                      isSelectedOrg ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
                    )}
                  >
                    {org.name}
                  </span>
                  <span className="mt-0.5 flex h-4 items-center gap-2">
                    <span className="text-xs text-cp-placeholder whitespace-nowrap">
                      {org.programs.length} Program{org.programs.length === 1 ? '' : 's'}
                    </span>
                    <MatchBadge count={org.matchCount} />
                  </span>
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'h-4.5 w-4.5 shrink-0 text-cp-dark-blue transition-transform',
                  isExpanded && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </button>

            {isExpanded && (
              <section
                id={programPanelId}
                data-testid={programPanelId}
                aria-label={`${org.name} programs`}
                className="flex flex-col gap-0.5 py-2 pl-4"
              >
                <p className="m-0 mb-1.5 ml-2 text-[11px] font-medium tracking-wider text-cp-placeholder uppercase">
                  Programs
                </p>
                {org.showAllPrograms && (
                  <button
                    type="button"
                    data-testid={`org-program-switcher-mobile-all-${org.id}`}
                    aria-current={isOrgSelected && selectedOrgId === org.id ? 'true' : undefined}
                    data-selected={isOrgSelected && selectedOrgId === org.id ? 'true' : undefined}
                    onClick={() => onSelectOrg(org.id)}
                    className={cn(
                      'flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-2 text-left transition-colors',
                      FOCUS_VISIBLE_RING,
                      isOrgSelected && selectedOrgId === org.id
                        ? 'bg-cp-light-purple'
                        : ROW_HOVER_CLASS,
                    )}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-light-grey bg-white">
                      <BriefcaseMedical
                        className="h-[18px] w-[18px] text-cp-dark-blue-2"
                        aria-hidden="true"
                      />
                    </div>
                    <span
                      className={cn(
                        'text-sm font-medium',
                        isOrgSelected && selectedOrgId === org.id
                          ? 'text-cp-bright-blue'
                          : 'text-cp-dark-blue',
                      )}
                    >
                      All Programs
                    </span>
                    {isOrgSelected && selectedOrgId === org.id && (
                      <Check className="ml-auto h-4 w-4 text-cp-bright-blue" aria-hidden="true" />
                    )}
                  </button>
                )}
                {org.visiblePrograms.map((program) => {
                  const isSelected = selectedProgramId === program.id && selectedOrgId === org.id
                  return (
                    <button
                      key={program.id}
                      type="button"
                      data-testid={`org-program-switcher-mobile-program-${program.id}`}
                      aria-current={isSelected ? 'true' : undefined}
                      data-selected={isSelected ? 'true' : undefined}
                      onClick={() => onSelectProgram(org.id, program.id)}
                      className={cn(
                        'flex h-10 w-full cursor-pointer items-center rounded-lg px-2 text-left transition-colors',
                        FOCUS_VISIBLE_RING,
                        isSelected ? 'bg-cp-light-purple' : ROW_HOVER_CLASS,
                      )}
                    >
                      <span
                        className={cn(
                          'text-sm font-medium',
                          isSelected ? 'text-cp-bright-blue' : 'text-cp-dark-blue',
                        )}
                      >
                        {program.name}
                      </span>
                      {isSelected && (
                        <Check className="ml-auto h-4 w-4 text-cp-bright-blue" aria-hidden="true" />
                      )}
                    </button>
                  )
                })}
              </section>
            )}
          </div>
        )
      })}
    </div>
  )
}
